import * as React from 'react';
import {
  Button as RACButton,
  DialogTrigger as RACDialogTrigger,
  Popover as RACPopover,
  Dialog as RACDialog,
  OverlayArrow,
  type PopoverProps as RACPopoverProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret Popover (handoff §10.8).
 * Primitive: RAC Popover + DialogTrigger.
 * --tvl-r3 (10px), --tvl-e2, 12px padding, max-width 320px. No arrow.
 * Grows from trigger's transform-origin.
 * All compound names preserved: Popover, PopoverTrigger, PopoverAnchor, PopoverClose, PopoverContent.
 */

export const PopoverTrigger = RACDialogTrigger;

/**
 * The trigger a Popover can actually be opened by.
 *
 * `PopoverTrigger` is RAC's `DialogTrigger`, which hands its press behaviour down through context —
 * a plain `<button>` child never receives it, so the popover silently never opens. Our own `Button`
 * is a plain button by design, and `react-aria-components` is not a dependency of the apps, so
 * consumers had no way to construct a working trigger at all. This is that missing piece.
 *
 * Deliberately unstyled apart from focus: callers wrap their own content (a badge, a value, a
 * count) and the trigger should not impose button chrome on it.
 */
export const PopoverTriggerButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof RACButton>
>(({ className, children, ...props }, ref) => (
  <RACButton
    ref={ref}
    className={cn(
      'inline-flex items-center gap-1 rounded-[var(--tvl-r2)] outline-none',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--tvl-ink)]',
      className,
    )}
    {...props}
  >
    {children}
  </RACButton>
));
PopoverTriggerButton.displayName = 'PopoverTriggerButton';

/** @deprecated No-op anchor shim — RAC handles placement natively. */
export const PopoverAnchor = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ children, ...props }, ref) => (
  <span ref={ref} style={{ display: 'contents' }} {...props}>
    {children}
  </span>
));
PopoverAnchor.displayName = 'PopoverAnchor';

export const PopoverClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'rounded-[var(--tvl-r1)] p-1 text-[var(--tvl-ink-3)]',
      'transition-colors duration-[var(--tvl-d-fast)]',
      'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
      className,
    )}
    {...props}
  >
    {children}
  </button>
));
PopoverClose.displayName = 'PopoverClose';

export interface PopoverContentProps extends Omit<RACPopoverProps, 'children' | 'className'> {
  className?: string;
  children?: React.ReactNode;
  /** Pixel offset from trigger — default 6. */
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}

export const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className, sideOffset = 6, children, ...props }, _ref) => (
    <RACPopover
      offset={sideOffset}
      className={cn(
        'z-[var(--tvl-z-popover)] w-72 max-w-[320px] outline-none',
        'bg-[var(--tvl-raised)] text-[var(--tvl-ink)]',
        'rounded-[var(--tvl-r3)] shadow-[var(--tvl-e2)]',
        'border border-[var(--tvl-hairline)]',
        'p-3',
        /* entrance: grow from origin */
        'entering:animate-[popover-enter_var(--tvl-d-base)_var(--tvl-ease-out)_forwards]',
        'exiting:animate-[popover-exit_var(--tvl-d-fast)_var(--tvl-ease-out)_forwards]',
        className,
      )}
      {...props}
    >
      <RACDialog className="outline-none">
        {children}
      </RACDialog>
    </RACPopover>
  ),
);
PopoverContent.displayName = 'PopoverContent';

/**
 * Root Popover — wraps PopoverTrigger + PopoverContent.
 * Consumers can also use PopoverTrigger directly.
 */
export const Popover = RACDialogTrigger;
